import React, { useState } from "react";
import { SketchPicker } from "react-color";

import "./Grid.css";

const GridLinesSettings = ({ setChartConfig, grid }) => {
  const { show, position, xaxis, yaxis, strokeDashArray, borderColor, row, column, padding } = grid;

  const [showBorderColorPicker, setShowBorderColorPicker] = useState(false);
  const [showRowColorPicker, setShowRowColorPicker] = useState(false);
  const [showColumnColorPicker, setShowColumnColorPicker] = useState(false);

  const handleGridChange = (key, value) => {
    setChartConfig((prevConfig) => ({
      ...prevConfig,
      grid: {
        ...prevConfig.grid,
        [key]: value
      }
    }));
  };

  const handlePaddingChange = (key, value) => {
    setChartConfig((prevConfig) => ({
      ...prevConfig,
      grid: {
        ...prevConfig.grid,
        padding: {
          ...prevConfig.grid.padding,
          [key]: Number(value)
        }
      }
    }));
  };

  return (
    <div className="grid-lines-container">
      {/* Title with Dropdown Icon */}
      <div className="grid-lines-header">
        <h3>Grid Lines</h3>
        <span className="grid-lines-dropdown-icon">▼</span>
      </div>

      {/* Grid Lines Settings */}
      <div>
        <div className="grid-lines-section">
          <label className="grid-lines-label">Show Grid Lines</label>
          <input type="checkbox" checked={show} onChange={(e) => handleGridChange("show", e.target.checked)} />
        </div>

        <div className="grid-lines-section">
          <label className="grid-lines-label">Grid Placement</label>
          <select value={position} onChange={(e) => handleGridChange("position", e.target.value)}>
            <option value="back">Back</option>
            <option value="front">Front</option>
          </select>
        </div>

        <div className="grid-lines-section">
          <label className="grid-lines-label">Line Dash</label>
          <input
            type="number"
            value={strokeDashArray || 0}
            onChange={(e) => handleGridChange("strokeDashArray", Number(e.target.value))}
          />
        </div>

        <div className="color-picker-wrapper">
          <label className="grid-lines-label">Grid Lines Color</label>
          <button className="color-picker-button" onClick={() => setShowBorderColorPicker(!showBorderColorPicker)}>
            <div className="color-swatch" style={{ backgroundColor: borderColor || "#000000" }} />
          </button>
          {showBorderColorPicker && (
            <div className="picker-popover">
              <div className="picker-cover" onClick={() => setShowBorderColorPicker(false)} />
              <SketchPicker
                color={borderColor || "#000000"}
                onChangeComplete={(color) => handleGridChange("borderColor", color.hex)}
              />
            </div>
          )}
        </div>

        <div className="color-picker-wrapper">
          <label className="grid-lines-label">Grid Row Colors</label>
          <button className="color-picker-button" onClick={() => setShowRowColorPicker(!showRowColorPicker)}>
            <div className="color-swatch" style={{ backgroundColor: row?.colors?.[0] || "#ffffff" }} />
          </button>
          {showRowColorPicker && (
            <div className="picker-popover">
              <div className="picker-cover" onClick={() => setShowRowColorPicker(false)} />
              <SketchPicker
                color={row?.colors?.[0] || "#ffffff"}
                onChangeComplete={(color) =>
                  handleGridChange("row", {
                    ...row,
                    colors: [color.hex]
                  })
                }
              />
            </div>
          )}
        </div>

        <div className="grid-lines-section">
          <label className="grid-lines-label">Grid Row Color Opacity</label>
          <input
            type="number"
            step="0.1"
            min="0"
            max="1"
            value={row?.opacity || 0.5}
            onChange={(e) =>
              handleGridChange("row", {
                ...row,
                opacity: e.target.value
              })
            }
          />
        </div>

        <div className="color-picker-wrapper">
          <label className="grid-lines-label">Grid Column Colors</label>
          <button className="color-picker-button" onClick={() => setShowColumnColorPicker(!showColumnColorPicker)}>
            <div className="color-swatch" style={{ backgroundColor: column?.colors?.[0] || "#ffffff" }} />
          </button>
          {showColumnColorPicker && (
            <div className="picker-popover">
              <div className="picker-cover" onClick={() => setShowColumnColorPicker(false)} />
              <SketchPicker
                color={column?.colors?.[0] || "#ffffff"}
                onChangeComplete={(color) =>
                  handleGridChange("column", {
                    ...column,
                    colors: [color.hex]
                  })
                }
              />
            </div>
          )}
        </div>

        <div className="grid-lines-section">
          <label className="grid-lines-label">Grid Column Color Opacity</label>
          <input
            type="number"
            step="0.1"
            min="0"
            max="1"
            value={column?.opacity}
            onChange={(e) =>
              handleGridChange("column", {
                ...column,
                opacity: e.target.value
              })
            }
          />
        </div>

        <div className="grid-lines-section">
          <label className="grid-lines-label">Show X-axis Lines</label>
          <input
            type="checkbox"
            checked={xaxis?.lines?.show}
            onChange={(e) => handleGridChange("xaxis", { lines: { show: e.target.checked } })}
          />
        </div>

        <div className="grid-lines-section">
          <label className="grid-lines-label">Show Y-axis Lines</label>
          <input
            type="checkbox"
            checked={yaxis?.lines?.show}
            onChange={(e) => handleGridChange("yaxis", { lines: { show: e.target.checked } })}
          />
        </div>

        {/* Grid Padding Settings */}
        <div className="grid-lines-section">
          <h4>Grid Padding</h4>
          <div className="grid-padding-settings">
            <div className="grid-padding-section">
              <label className="grid-lines-label">Top</label>
              <input
                type="number"
                value={padding?.top || 0}
                onChange={(e) => handlePaddingChange("top", e.target.value)}
              />
            </div>

            <div className="grid-padding-section">
              <label className="grid-lines-label">Right</label>
              <input
                type="number"
                value={padding?.right || 25}
                onChange={(e) => handlePaddingChange("right", e.target.value)}
              />
            </div>

            <div className="grid-padding-section">
              <label className="grid-lines-label">Bottom</label>
              <input
                type="number"
                value={padding?.bottom || 0}
                onChange={(e) => handlePaddingChange("bottom", e.target.value)}
              />
            </div>

            <div className="grid-padding-section">
              <label className="grid-lines-label">Left</label>
              <input
                type="number"
                value={padding?.left || 15}
                onChange={(e) => handlePaddingChange("left", e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GridLinesSettings;
